(() => {
  const ROOT_CLASS = "header-search-overlay-open";

  /** @returns {HTMLElement[]} */
  const getInstances = () =>
    Array.from(document.querySelectorAll("[data-header-search-inline]"));

  /** @param {HTMLElement} instance */
  const getInput = (instance) =>
    instance.querySelector("[data-header-search-input]");

  /** @param {HTMLElement} instance */
  const getOverlay = (instance) =>
    instance.querySelector("[data-header-search-overlay]");

  /** @param {HTMLElement} instance */
  const getBackdrop = (instance) =>
    instance.querySelector("[data-header-search-backdrop]");

  /** @param {HTMLElement} instance */
  const getPopularList = (instance) =>
    instance.querySelector("[data-header-search-popular-list]");

  /** @param {HTMLElement} instance */
  const getFilters = (instance) =>
    instance.querySelector("[data-header-search-filters]");

  /** @param {HTMLElement} instance */
  const getNoResultsEl = (instance) =>
    instance.querySelector("[data-header-search-no-results]");

  const ACTIVE_COLLECTION_ATTR = "data-header-search-active-collection";
  const MOBILE_MEDIA_QUERY = "(max-width: 989px)";

  let collectionsCachePromise;
  const collectionProductsCache = new Map();
  const collectionProductsDataCache = new Map();
  let lockedScrollY = 0;
  let bodyScrollLocked = false;

  /** @param {HTMLElement} instance */
  const getResultsRoot = (instance) =>
    instance.querySelector("[data-header-search-results]");

  /** @param {HTMLElement} instance */
  const getPredictiveContainer = (instance) =>
    instance.querySelector("[data-predictive-search]");

  /** @param {HTMLElement} instance */
  const getPredictiveStatus = (instance) =>
    instance.querySelector(".predictive-search-status");

  /** @param {HTMLElement} instance */
  const getCollectionResultsContainer = (instance) => {
    const root = getResultsRoot(instance);
    if (!root) return null;

    let el = root.querySelector("[data-header-search-collection-results]");
    if (el) return el;

    el = document.createElement("div");
    el.className = "header-search-inline__collection-results";
    el.setAttribute("data-header-search-collection-results", "");
    el.hidden = true;

    const predictive = getPredictiveContainer(instance);
    if (predictive) root.insertBefore(el, predictive);
    else root.appendChild(el);
    return el;
  };

  const setPredictiveVisible = (instance, visible) => {
    const predictive = getPredictiveContainer(instance);
    if (predictive) predictive.hidden = !visible;
    const status = getPredictiveStatus(instance);
    if (status) status.hidden = !visible;
  };

  const fetchAllCollections = async () => {
    const limit = 250;
    const maxPages = 10;
    const seenIds = new Set();
    const out = [];

    for (let page = 1; page <= maxPages; page += 1) {
      const res = await fetch(`/collections.json?limit=${limit}&page=${page}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) break;

      const data = await res.json();
      const items = Array.isArray(data?.collections) ? data.collections : [];
      if (!items.length) break;

      // Stop if endpoint ignores pagination and repeats.
      const before = out.length;
      items.forEach((c) => {
        const id = c?.id;
        const title = typeof c?.title === "string" ? c.title.trim() : "";
        if (!id || !title) return;
        if (seenIds.has(id)) return;
        seenIds.add(id);
        out.push({ id, title, handle: c?.handle || "" });
      });

      if (out.length === before) break;
      if (items.length < limit) break;
    }

    return out;
  };

  const getAllCollections = () => {
    if (!collectionsCachePromise) {
      collectionsCachePromise = fetchAllCollections().catch(() => []);
    }
    return collectionsCachePromise;
  };

  /** @param {HTMLElement} instance */
  const renderCollectionChips = async (instance) => {
    const filters = getFilters(instance);
    if (!filters) return;

    // Remove previous auto-rendered chips
    Array.from(
      filters.querySelectorAll("[data-header-search-collection-auto]"),
    ).forEach((el) => el.remove());

    const collections = await getAllCollections();
    if (!collections.length) return;

    // If there are already Liquid-rendered chips, replace them to ensure "all collections".
    filters.innerHTML = "";
    const fragment = document.createDocumentFragment();
    collections.forEach((c) => {
      if (!c.handle) return;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "header-search-inline__filter";
      btn.setAttribute("data-header-search-collection-handle", c.handle);
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("data-header-search-collection-auto", "true");
      btn.textContent = c.title;
      fragment.appendChild(btn);
    });
    filters.appendChild(fragment);

    syncActiveCollectionUI(instance);
  };

  const getActiveCollectionHandle = (instance) =>
    instance.getAttribute(ACTIVE_COLLECTION_ATTR) || "";

  const setActiveCollectionHandle = (instance, handle) => {
    if (handle) {
      instance.setAttribute(ACTIVE_COLLECTION_ATTR, handle);
    } else {
      instance.removeAttribute(ACTIVE_COLLECTION_ATTR);
    }
    syncActiveCollectionUI(instance);
  };

  const syncActiveCollectionUI = (instance) => {
    const active = getActiveCollectionHandle(instance);
    const buttons = Array.from(
      instance.querySelectorAll("[data-header-search-collection-handle]"),
    );
    buttons.forEach((btn) => {
      const handle =
        btn.getAttribute("data-header-search-collection-handle") || "";
      const isActive = !!active && handle === active;
      btn.classList.toggle("is-active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  };

  const getCollectionProductHandles = (handle) => {
    if (!handle) return Promise.resolve(new Set());
    if (collectionProductsCache.has(handle))
      return collectionProductsCache.get(handle);

    const promise = (async () => {
      const limit = 250;
      const maxPages = 10;
      const handles = new Set();

      for (let page = 1; page <= maxPages; page += 1) {
        const res = await fetch(
          `/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}&page=${page}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        if (!res.ok) break;
        const data = await res.json();
        const products = Array.isArray(data?.products) ? data.products : [];
        if (!products.length) break;

        const before = handles.size;
        products.forEach((p) => {
          if (typeof p?.handle === "string" && p.handle) handles.add(p.handle);
        });

        if (handles.size === before) break;
        if (products.length < limit) break;
      }

      return handles;
    })().catch(() => new Set());

    collectionProductsCache.set(handle, promise);
    return promise;
  };

  const getCollectionProducts = (handle) => {
    if (!handle) return Promise.resolve([]);
    if (collectionProductsDataCache.has(handle))
      return collectionProductsDataCache.get(handle);

    const promise = (async () => {
      const limit = 250;
      const maxPages = 10;
      const out = [];

      for (let page = 1; page <= maxPages; page += 1) {
        const res = await fetch(
          `/collections/${encodeURIComponent(handle)}/products.json?limit=${limit}&page=${page}`,
          {
            headers: { Accept: "application/json" },
          },
        );
        if (!res.ok) break;
        const data = await res.json();
        const products = Array.isArray(data?.products) ? data.products : [];
        if (!products.length) break;

        products.forEach((p) => {
          const productHandle = typeof p?.handle === "string" ? p.handle : "";
          const title = typeof p?.title === "string" ? p.title : "";
          const images = Array.isArray(p?.images) ? p.images : [];
          const featured = images[0];
          const imgSrc = typeof featured?.src === "string" ? featured.src : "";
          if (!productHandle || !title) return;
          out.push({ handle: productHandle, title, imgSrc });
        });

        if (products.length < limit) break;
      }

      return out;
    })().catch(() => []);

    collectionProductsDataCache.set(handle, promise);
    return promise;
  };

  const renderCollectionResults = async (instance) => {
    const input = getInput(instance);
    const query = (input?.value || "").trim();
    const active = getActiveCollectionHandle(instance);

    const container = getCollectionResultsContainer(instance);
    if (!container) return;

    const noResults = getNoResultsEl(instance);

    if (query) {
      container.hidden = true;
      container.innerHTML = "";
      setPredictiveVisible(instance, true);
      if (noResults) noResults.hidden = true;
      return;
    }

    if (!active) {
      container.hidden = true;
      container.innerHTML = "";
      setPredictiveVisible(instance, true);
      if (noResults) noResults.hidden = true;
      return;
    }

    // Query is empty but a collection is selected: show collection products.
    setPredictiveVisible(instance, false);
    container.hidden = false;
    container.innerHTML =
      '<div class="predictive-search__loading-state"></div>';

    const products = await getCollectionProducts(active);
    const ul = document.createElement("ul");
    ul.className =
      "predictive-search__results-list predictive-search__results-list--products list-unstyled";
    ul.setAttribute("role", "list");

    products.forEach((p, idx) => {
      const li = document.createElement("li");
      li.className = "predictive-search__list-item";
      li.setAttribute("role", "listitem");
      li.setAttribute("aria-selected", "false");
      li.id = `am-collection-result-${idx + 1}`;

      const a = document.createElement("a");
      a.className = "header-search-inline__collection-item link link--text";
      a.href = `/products/${encodeURIComponent(p.handle)}`;
      a.tabIndex = -1;

      const card = document.createElement("div");
      card.className = "header-search-inline__collection-card";

      const media = document.createElement("div");
      media.className = "header-search-inline__collection-media";
      if (p.imgSrc) {
        const img = document.createElement("img");
        img.className = "header-search-inline__collection-image";
        img.loading = "lazy";
        img.decoding = "async";
        img.src = p.imgSrc;
        img.alt = p.title;
        media.appendChild(img);
      }

      const title = document.createElement("div");
      title.className = "header-search-inline__collection-title";
      title.textContent = p.title;

      card.appendChild(media);
      card.appendChild(title);
      a.appendChild(card);
      li.appendChild(a);
      ul.appendChild(li);
    });

    container.innerHTML = "";
    container.appendChild(ul);

    // When query is empty, predictive-search would normally show nothing.
    // If the selected collection has no products, show the existing no-results message.
    if (noResults) noResults.hidden = products.length > 0;
  };

  const extractProductHandleFromItem = (item) => {
    const link = item.querySelector('a[href*="/products/"]');
    const href = link?.getAttribute("href") || "";
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : "";
  };

  const applyCollectionFilterToPredictiveResults = async (instance) => {
    const active = getActiveCollectionHandle(instance);
    const list = instance.querySelector(
      ".predictive-search__results-list--products",
    );
    if (!list) return;

    const items = Array.from(
      list.querySelectorAll(".predictive-search__list-item"),
    );
    if (!active) {
      items.forEach((li) => {
        li.hidden = false;
        li.removeAttribute("data-header-search-hidden-by-collection");
      });
      return;
    }

    const allowed = await getCollectionProductHandles(active);

    items.forEach((li) => {
      const handle = extractProductHandleFromItem(li);
      if (!handle) return;
      const show = allowed.has(handle);
      li.hidden = !show;
      if (!show)
        li.setAttribute("data-header-search-hidden-by-collection", "true");
      else li.removeAttribute("data-header-search-hidden-by-collection");
    });

    updateNoResultsState(instance);
  };

  const updateNoResultsState = (instance) => {
    const el = getNoResultsEl(instance);
    if (!el) return;

    const query = (getInput(instance)?.value || "").trim();
    if (!query) {
      el.hidden = true;
      return;
    }

    const list = instance.querySelector(
      ".predictive-search__results-list--products",
    );
    if (!list) {
      el.hidden = false;
      return;
    }

    const items = Array.from(
      list.querySelectorAll(".predictive-search__list-item"),
    );
    const visibleCount = items.filter((li) => !li.hidden).length;
    el.hidden = visibleCount > 0;
  };

  const patchPredictiveSearchForCollectionFilter = () => {
    const Predictive = window.customElements?.get?.("predictive-search");
    if (!Predictive || !Predictive.prototype) {
      // predictive-search may be defined after DOMContentLoaded depending on asset loading order.
      if (
        !window.__amHeaderSearchCollectionPatchQueued &&
        window.customElements?.whenDefined
      ) {
        window.__amHeaderSearchCollectionPatchQueued = true;
        window.customElements.whenDefined("predictive-search").then(() => {
          patchPredictiveSearchForCollectionFilter();
        });
      }
      return;
    }
    if (Predictive.prototype.__amHeaderSearchCollectionPatched) return;

    const originalRender = Predictive.prototype.renderSearchResults;
    if (typeof originalRender === "function") {
      Predictive.prototype.renderSearchResults = function (...args) {
        const result = originalRender.apply(this, args);
        if (this.hasAttribute("data-header-search-inline")) {
          applyCollectionFilterToPredictiveResults(this);
          updateNoResultsState(this);
        }
        return result;
      };
    }

    const originalOnFocusOut = Predictive.prototype.onFocusOut;
    if (typeof originalOnFocusOut === "function") {
      Predictive.prototype.onFocusOut = function (...args) {
        const isHeaderOverlay = this.hasAttribute("data-header-search-inline");
        const isMobile = window.matchMedia?.("(max-width: 989px)")?.matches;
        const isOverlayOpen =
          this.getAttribute("data-search-overlay-open") === "true";

        // Mobile UX: never close predictive on focusout while the search overlay is open.
        // Safari/Firefox can fire focusout when the keyboard hides.
        if (isHeaderOverlay && isMobile && isOverlayOpen) {
          return;
        }

        return originalOnFocusOut.apply(this, args);
      };
    }

    Predictive.prototype.__amHeaderSearchCollectionPatched = true;
  };

  const anyOverlayOpen = () =>
    getInstances().some(
      (el) => el.getAttribute("data-search-overlay-open") === "true",
    );

  const lockBodyScroll = () => {
    if (bodyScrollLocked) return;
    if (!window.matchMedia?.(MOBILE_MEDIA_QUERY)?.matches) return;

    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${lockedScrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    bodyScrollLocked = true;
  };

  const unlockBodyScroll = () => {
    if (!bodyScrollLocked) return;

    const body = document.body;
    body.style.position = "";
    body.style.top = "";
    body.style.left = "";
    body.style.right = "";
    body.style.width = "";
    window.scrollTo(0, lockedScrollY);
    bodyScrollLocked = false;
  };

  const setRootState = () => {
    const root = document.documentElement;
    if (anyOverlayOpen()) {
      root.classList.add(ROOT_CLASS);
      lockBodyScroll();
    } else {
      root.classList.remove(ROOT_CLASS);
      unlockBodyScroll();
    }
  };

  /** @param {HTMLElement} instance */
  const openOverlay = (instance) => {
    if (instance.getAttribute("data-search-overlay-open") === "true") {
      const input = getInput(instance);
      if (input) {
        input.focus();
        try {
          const end = input.value.length;
          input.setSelectionRange(end, end);
        } catch {
          // ignore
        }
      }
      return;
    }

    getInstances().forEach((el) => {
      if (el === instance) return;
      if (el.getAttribute("data-search-overlay-open") !== "true") return;
      el.removeAttribute("data-search-overlay-open");
      const otherBackdrop = getBackdrop(el);
      if (otherBackdrop) otherBackdrop.hidden = true;
    });

    instance.setAttribute("data-search-overlay-open", "true");

    const backdrop = getBackdrop(instance);
    if (backdrop) backdrop.hidden = false;

    setRootState();

    // Prefer using the existing trapFocus util (defined in global.js)
    const overlay = getOverlay(instance);
    const input = getInput(instance);
    if (overlay && input && typeof window.trapFocus === "function") {
      window.trapFocus(overlay, input);
    }
    // Populate collection chips lazily and cache across opens.
    renderCollectionChips(instance);
  };

  const clearSearchState = (instance) => {
    // Clear active collection filter
    setActiveCollectionHandle(instance, "");

    // Clear query
    const input = getInput(instance);
    if (input) {
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    // Ensure any previously hidden items become visible
    applyCollectionFilterToPredictiveResults(instance);

    const container = getCollectionResultsContainer(instance);
    if (container) {
      container.hidden = true;
      container.innerHTML = "";
    }
    setPredictiveVisible(instance, true);
  };

  /** @param {HTMLElement} instance */
  const closeOverlay = (instance) => {
    clearSearchState(instance);
    instance.removeAttribute("data-search-overlay-open");

    const backdrop = getBackdrop(instance);
    if (backdrop) backdrop.hidden = true;

    if (typeof window.removeTrapFocus === "function") {
      window.removeTrapFocus();
    }

    setRootState();

    const input = getInput(instance);
    if (input) {
      input.focus();
    }
  };

  /** @param {HTMLElement} instance */
  const closeOverlayIfOpen = (instance) => {
    if (instance.getAttribute("data-search-overlay-open") === "true")
      closeOverlay(instance);
  };

  const setSearchTerm = (instance, term, { markActiveFilter = false } = {}) => {
    const input = getInput(instance);
    if (!input) return;

    input.value = term;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.focus();

    // markActiveFilter deprecated: collection filters are handled separately
  };

  const initInstance = (instance) => {
    const input = getInput(instance);
    const closeBtn = instance.querySelector("[data-header-search-close]");
    const form = instance.querySelector("[data-header-search-form]");

    instance.addEventListener(
      "focusout",
      (event) => {
        const isMobile = window.matchMedia?.("(max-width: 989px)")?.matches;
        const isOverlayOpen =
          instance.getAttribute("data-search-overlay-open") === "true";
        const hasQuery = !!(getInput(instance)?.value || "").trim();

        if (!isMobile || !isOverlayOpen || !hasQuery) return;

        // If focus is still moving inside the search instance, let native logic continue.
        if (event.relatedTarget && instance.contains(event.relatedTarget))
          return;

        // Prevent predictive-search default focusout close on mobile keyboard dismiss.
        event.stopImmediatePropagation();
      },
      true,
    );

    if (input) {
      input.addEventListener("click", () => {
        openOverlay(instance);
      });

      input.addEventListener("input", () => {
        // When the user starts typing, always show predictive search.
        renderCollectionResults(instance);
      });

      input.addEventListener("blur", () => {
        const queryOnBlur = (getInput(instance)?.value || "").trim();

        // Mobile browsers can close predictive results when the keyboard hides.
        // Keep results visible while overlay is open and query is not empty.
        setTimeout(() => {
          const isMobile = window.matchMedia?.("(max-width: 989px)")?.matches;
          const isOverlayOpen =
            instance.getAttribute("data-search-overlay-open") === "true";
          const hasQuery = !!queryOnBlur;

          if (!isMobile || !isOverlayOpen || !hasQuery) return;

          const predictiveContainer = getPredictiveContainer(instance);
          const predictiveHidden =
            !predictiveContainer ||
            predictiveContainer.hidden ||
            window.getComputedStyle(predictiveContainer).display === "none";

          const isPredictiveOpen = instance.getAttribute("open") === "true";
          const hasRenderedResults =
            instance.getAttribute("results") === "true";

          // Recover from browser-specific blur/focusout sequences that collapse results.
          if (
            (!isPredictiveOpen || predictiveHidden || !hasRenderedResults) &&
            typeof instance.getSearchResults === "function"
          ) {
            instance.getSearchResults(queryOnBlur);
          }

          if (
            typeof instance.open === "function" &&
            instance.getAttribute("results") === "true"
          ) {
            instance.open();
          }

          setPredictiveVisible(instance, true);
          updateNoResultsState(instance);
        }, 0);
      });

      // Prevent Enter from submitting the form (no redirect to search results page).
      input.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        openOverlay(instance);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener("click", () => closeOverlayIfOpen(instance));
    }

    // Clicking inside buttons that represent terms
    instance.addEventListener("click", (event) => {
      const collectionButton = event.target.closest(
        "[data-header-search-collection-handle]",
      );
      if (collectionButton) {
        event.preventDefault();

        openOverlay(instance);
        const handle =
          collectionButton.getAttribute(
            "data-header-search-collection-handle",
          ) || "";
        const active = getActiveCollectionHandle(instance);
        setActiveCollectionHandle(instance, active === handle ? "" : handle);

        // If query is empty, show results for the selected collection.
        // If query exists, keep using predictive results and apply the filter.
        const query = (getInput(instance)?.value || "").trim();
        if (!query) {
          renderCollectionResults(instance);
        } else {
          // Refresh current results / re-apply filter immediately
          applyCollectionFilterToPredictiveResults(instance);
        }

        // Keep the query editable after using filters.
        const inputEl = getInput(instance);
        if (inputEl) {
          inputEl.focus();
          try {
            const end = inputEl.value.length;
            inputEl.setSelectionRange(end, end);
          } catch {
            // ignore
          }
        }
        return;
      }

      const termButton = event.target.closest("[data-header-search-term]");
      if (!termButton) return;

      event.preventDefault();
      const term = termButton.getAttribute("data-header-search-term") || "";
      const isFilter = termButton.classList.contains(
        "header-search-inline__filter",
      );

      openOverlay(instance);
      setSearchTerm(instance, term, { markActiveFilter: isFilter });
    });

    // Track submitted searches (per-user in this browser via localStorage)
    if (form) {
      form.addEventListener("submit", (event) => {
        // Never navigate away from the current page when using header search.
        event.preventDefault();

        openOverlay(instance);

        const inputEl = getInput(instance);
        if (inputEl)
          inputEl.dispatchEvent(new Event("input", { bubbles: true }));
      });
    }

    // Keep collection filter active while typing; filtering is applied after each predictive render.
  };

  const init = () => {
    patchPredictiveSearchForCollectionFilter();
    getInstances().forEach(initInstance);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
